import { matchAnyInArray } from "~/lib/utils/helper/matchAnyInArray";
import { useMemo } from "react";
import { useMesraHistory } from "~/services/queries/loyalty.queries";
import { useIndexTransactions } from "~/services/queries/payments.queries";
import { ITransactionsApiTypes } from "~/lib/types/variables.type";

interface ITransactionProps {
  enabled: boolean | false;
  perPage?: number;
  referenceType?: string;
  transactionType: ITransactionsApiTypes;
}

export const useTransactions = ({
  enabled,
  perPage,
  referenceType,
  transactionType,
}: ITransactionProps) => {
  const unixTimestamp = Date.now() - (180 * 1000 * 60 * 60 * 24);
  const fromDateFilter = new Date(unixTimestamp).toISOString().split('T')[0];
  const { data: paymentData, isLoading: isLoadingPayment } =
    useIndexTransactions({
      enabled:
        (enabled &&
          matchAnyInArray(transactionType, [
            ITransactionsApiTypes.PAYMENT,
            ITransactionsApiTypes.ALL,
          ])) ||
        false,
      referenceType: referenceType,
      from: fromDateFilter,
      perPage: perPage,
    });

  const { data: mesraData, isLoading: isLoadingMesra } = useMesraHistory({
    enabled:
      (enabled &&
        matchAnyInArray(transactionType, [
          ITransactionsApiTypes.LOYALTY,
          ITransactionsApiTypes.ALL,
        ])) ||
      false,
    from: fromDateFilter,
    referenceType: referenceType,
    perPage: perPage,
  });

  const isLoading = isLoadingPayment || isLoadingMesra;

  //TODO - add contigency check in case one of the queries is not loaded

  // returns respective transaction based on transactionType
  const combinedTransactions = useMemo(() => {
    if (isLoading) {
      return undefined;
    }

    // key should match ITransactionApiTypes values
    const transactions = {
      PAYMENT: () => paymentData,
      LOYALTY: () => mesraData,
      ALL: () =>
        [...(paymentData ?? []), ...(mesraData ?? [])].sort((a, b) =>
          a.createdAt > b.createdAt ? -1 : 1
        ),
    };
    return transactions[transactionType]();
  }, [paymentData, mesraData, isLoading, transactionType]);
  return { isLoading, data: combinedTransactions };
};
